import {collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, where} from "@firebase/firestore";
import {firestore} from "@/services/firebase.service";
import {CrosswordDataType, CrosswordFileType} from "@/types/crosswords";
import cookieService from "@/services/cookie.service";
import {DEFAULT_WORD, WordType} from "@/types/words";
import {createQuestionMaps, crosswordStringToBoard} from "@/utils/crossword";
import id from "@/pages/chg-admin/crosswords/[id]";
import wordsService from "@/services/words.service";
import {Timestamp} from "@firebase/firestore-types";

const listCrosswords = async (category: 'upcoming' | 'unscheduled' | 'past' | 'scheduled'): Promise<CrosswordDataType[]> => {
  const crosswordsCollection  = collection(firestore, 'crosswords')

  const constraints = []
  if (category === 'upcoming') constraints.push(where('date', '>', new Date()))
  if (category === 'unscheduled') constraints.push(where('date', '==', null))
  if (category === 'past') constraints.push(where('date', '<', new Date()))
  if (category === 'scheduled') constraints.push(where('date', '!=', null))
  const q = query(crosswordsCollection, ...constraints, )

  const snapshots = await getDocs(q)

  return snapshots.docs.map(doc => ({id: doc.id, ...doc.data()} as CrosswordDataType))

}

const getCrossword = async (id: string): Promise<CrosswordDataType | null> => {
  const crosswordsCollection  = collection(firestore, 'crosswords')

  const docRef = doc(crosswordsCollection, id)
  const docSnap = await getDoc(docRef)

  return docSnap.exists() ? {id: docSnap.id, ...docSnap.data()} as CrosswordDataType : null
}

const getCrosswordSubscription = async (id: string, f: (c: CrosswordDataType | null) => void) => {
  const unsub = onSnapshot(doc(firestore, 'crosswords', id), (doc) => {
    f(doc.exists() ? {id: doc.id, ...doc.data()} as CrosswordDataType : null)
  })
  return unsub
}

const createCrossword = async (data: CrosswordFileType) => {
  const crosswordsCollection  = collection(firestore, 'crosswords')

  const newDocRef = doc(crosswordsCollection)
  await setDoc(newDocRef, {
    ...data,
    date: null,
    createdBy: cookieService.get('displayName'),
    createdAt: new Date(),
  })

  const newDoc = await getDoc(newDocRef)

  return {id: newDoc.id, ...newDoc.data()} as CrosswordDataType
}

const patchCrossword = async (id: string, data: Partial<CrosswordFileType>) => {
  const crosswordsCollection  = collection(firestore, 'crosswords')

  const docRef = doc(crosswordsCollection, id)

  await setDoc(docRef, {
    ...data,
    updatedAt: new Date(),
    updatedBy: cookieService.get('displayName'),
  }, {merge: true})

  const newDoc = await getDoc(docRef)

  return {id: newDoc.id, ...newDoc.data()} as CrosswordDataType
}

const scheduleCrossword = async (crossword: CrosswordDataType, date: Date | null) => {
  const board = crosswordStringToBoard(crossword.grid)
  const crosswordMaps = createQuestionMaps(board, crossword.acrossAnswers, crossword.downAnswers)

  const updateWord = async (word: string, clue: string) => {
    let wordData = await wordsService.getWord(word)
    if (!wordData) wordData = await wordsService.createWord({...DEFAULT_WORD, word, category: 'crossword', hints: clue ? [clue] : [], lastUsed: date as any})
    else await wordsService.updateWord(word, {lastUsed: date as any, hints: clue && !wordData.hints.includes(clue) ? [...wordData.hints, clue] : wordData.hints})
  }

  for (const questionNo in crosswordMaps.acrossWordMap) {
    await updateWord(crosswordMaps.acrossWordMap[questionNo], crosswordMaps.acrossQuestionMap[questionNo])
  }
  for (const questionNo in crosswordMaps.downWordMap) {
    await updateWord(crosswordMaps.downWordMap[questionNo], crosswordMaps.downQuestionMap[questionNo])
  }


  return patchCrossword(crossword.id, {date})
}

const manuallyPublishCrossword = async (crosswordId: string) => {
  const crossword = await getCrossword(crosswordId)
  if (!crossword) throw new Error('Crossword not found')

  const liveCrosswordCollection = collection(firestore, 'liveCrossword')

  const docRef = doc(liveCrosswordCollection, 'today')

  await setDoc(docRef, {
    id: crosswordId,
    setBy: cookieService.get('displayName'),
    setAt: new Date(),
  })
}

const getCurrentCrosswordId = async () => {
  const liveCrosswordCollection = collection(firestore, 'liveCrossword')

  const docRef = doc(liveCrosswordCollection, 'today')
  const docSnap = await getDoc(docRef)

  return docSnap.exists() ? docSnap.data().id as string : null
}

const saveClue = async (clue: string, crossword: CrosswordDataType, direction: 'down' | 'across', index: number, word: string) => {
  if (!crossword.id) return

  // If the crossword is published, update the word hints
  if (crossword.date) {
    const wordData = await wordsService.getWord(word)
    if (wordData) await wordsService.updateWord(word, {hints: [...wordData.hints, clue]})
  }

  if (direction === 'down') {
    const newDownAnswers = [...crossword.downAnswers]
    newDownAnswers[index] = clue
    return patchCrossword(crossword.id, {downAnswers: newDownAnswers})
  }
  else if (direction === 'across') {
    const newAcrossAnswers = [...crossword.acrossAnswers]
    newAcrossAnswers[index] = clue
    return patchCrossword(crossword.id, {acrossAnswers: newAcrossAnswers})
  }
}

const getLiveCrossword = async () => {
  const liveCrosswordCollection = collection(firestore, 'liveCrossword')

  const docRef = doc(liveCrosswordCollection, 'today')
  const docSnap = await getDoc(docRef)
  const id = docSnap.exists() ? docSnap.data().id : null
  return await getCrossword(id)
}


const CrosswordService = {
  listCrosswords,
  getCrossword,
  createCrossword,
  patchCrossword,
  getCrosswordSubscription,
  saveClue,
  scheduleCrossword,
  manuallyPublishCrossword,
  getCurrentCrosswordId,
  getLiveCrossword,
}

export default CrosswordService